import React from "react";
import { Typography, TypographyProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { createCustomTheme } from "styles/theming/theme";

// Define available color options
type ColorOption =
  | "blue"
  | "purple"
  | "lightBlue"
  | "oceanBlue"
  | "lightGreen"
  | "orange"
  | "warning"
  | "textSecondary"
  | "textPrimary"
  | "grey"
  | "white"
  | "black"
  | "red"
  | "redLight"
  | "green";

// Props interface extending Material-UI Typography props
export interface TextProps extends Omit<TypographyProps, "color" | "sx"> {
  id?: string;
  children?: React.ReactNode;
  classes?: string;
  variant?: TypographyProps["variant"];
  center?: boolean;
  color?: ColorOption | string;
  marginY?: number | string;
  whiteSpace?: "normal" | "nowrap" | "pre" | "pre-line" | "pre-wrap" | "initial" | "inherit" | "unset";
  sx?: SxProps<Theme>;
  title?: string;
}

const Text: React.FC<TextProps> = ({
  id,
  children,
  classes,
  variant = "h2",
  center = false,
  color,
  marginY = 0,
  whiteSpace = "nowrap",
  sx = {},
  title,
  ...props
}) => {
  const theme = createCustomTheme();

  // TODO - fix theme reference -  Blue purple and green are extensions we made.
  const colors: Record<ColorOption, string> = {
    blue: theme?.palette?.common?.blue || "#1976d2",
    purple: theme?.palette?.common?.purple || "#9c27b0",
    lightBlue: theme?.palette?.common?.blue || "#03a9f4",
    oceanBlue: "#315FFF",
    lightGreen: "#5EC4B2",
    orange: "#FF9314",
    warning: "#ED6C02",
    textSecondary: theme?.palette?.text?.secondary || "#666",
    textPrimary: theme?.palette?.text?.primary || "#000",
    grey: theme?.palette?.text?.secondary || "#666",
    white: theme?.palette?.common?.white || "#fff",
    black: theme?.palette?.common?.black || "#000",
    red: theme.palette.error.main,
    redLight: theme.palette.error.light,
    green: theme?.palette?.common?.green || "#4caf50",
  };

  // If color is a predefined ColorOption, use the mapped value
  // Otherwise, treat it as a direct color value (e.g., theme.palette.primary.main)
  const textColor = color ? colors[color as ColorOption] || color : "unset";

  return (
    <Typography
      data-testid={id}
      variant={variant}
      align={center ? "center" : undefined}
      className={classes}
      sx={{
        display: "inline-block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        // Conditional defaults
        ...(marginY && { my: marginY }),
        whiteSpace,
        ...(color && { color: textColor }),
        ...sx,
      }}
      {...props}
      title={title || (typeof children === "string" ? children : undefined)}>
      {children}
    </Typography>
  );
};

export default Text;
